import React, { useState, useEffect, useCallback } from 'react';
import { getInvitaciones, crearInvitacion, eliminarInvitacion, getComunidades } from '../services/api';
import ConfirmModal from '../components/ConfirmModal';

const BASE_URL = window.location.origin;

function Invitaciones({ user }) {
  const [invitaciones, setInvitaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comunidades, setComunidades] = useState([]);

  const [form, setForm] = useState({
    comunidad_id: user?.comunidad_id || '',
    email: '',
    usos_maximos: 1,
    dias_expiracion: 7,
  });
  const [creando, setCreando] = useState(false);
  const [linkCopiado, setLinkCopiado] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

  const cargarInvitaciones = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getInvitaciones(
        user?.rol === 'administrador' ? (form.comunidad_id || null) : null
      );
      setInvitaciones(res.data);
    } catch (e) {
      setError('Error al cargar invitaciones');
    } finally {
      setLoading(false);
    }
  }, [user, form.comunidad_id]);

  useEffect(() => {
    cargarInvitaciones();
    if (user?.rol === 'administrador') {
      getComunidades().then(r => setComunidades(r.data)).catch(() => {});
    }
  }, [cargarInvitaciones, user]);

  const handleCrear = async (e) => {
    e.preventDefault();
    setCreando(true);
    setError('');
    try {
      await crearInvitacion({
        comunidad_id: form.comunidad_id || undefined,
        email: form.email || undefined,
        usos_maximos: Number(form.usos_maximos),
        dias_expiracion: form.dias_expiracion ? Number(form.dias_expiracion) : undefined,
      });
      setForm(f => ({ ...f, email: '', usos_maximos: 1, dias_expiracion: 7 }));
      cargarInvitaciones();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al crear invitacion');
    } finally {
      setCreando(false);
    }
  };

  const openConfirm = (title, message, onConfirm) => {
    setConfirmModal({ isOpen: true, title, message, onConfirm });
  };
  const closeConfirm = () => setConfirmModal(prev => ({ ...prev, isOpen: false }));

  const handleEliminar = (id) => {
    openConfirm(
      'Eliminar invitación',
      '¿Estás seguro de que quieres eliminar esta invitación?',
      async () => {
        closeConfirm();
        try {
          await eliminarInvitacion(id);
          cargarInvitaciones();
        } catch (e) {
          setError('Error al eliminar');
        }
      }
    );
  };

  const copiarLink = (token) => {
    const link = BASE_URL + '/registro?invite=' + token;
    navigator.clipboard.writeText(link).then(() => {
      setLinkCopiado(token);
      setTimeout(() => setLinkCopiado(null), 2500);
    });
  };

  const formatFecha = (f) => {
    if (!f) return 'Sin limite';
    return new Date(f).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const estaAgotada = (inv) => inv.usos_actuales >= inv.usos_maximos;
  const estaExpirada = (inv) => inv.expira_en && new Date(inv.expira_en) < new Date();

  return (
    <div style={{ padding: '24px 20px', maxWidth: 900, margin: '0 auto' }}>

      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
          Links de Invitacion
        </h1>
        <p style={{ color: 'var(--text-secondary)', marginTop: 6, fontSize: '0.9rem' }}>
          Genera links unicos para que los vecinos se registren directamente en su comunidad.
        </p>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: 20 }}>{error}</div>
      )}

      <div className="card" style={{ padding: '20px 24px', marginBottom: 28 }}>
        <h3 style={{ margin: '0 0 18px', fontSize: '1rem', fontWeight: 600, color: 'var(--primary-color, #2563eb)' }}>
          Nueva invitacion
        </h3>
        <form onSubmit={handleCrear}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>

            {user && user.rol === 'administrador' && (
              <div className="form-group" style={{ flex: '1 1 180px', marginBottom: 0 }}>
                <label className="form-label">Comunidad</label>
                <select
                  className="form-control"
                  value={form.comunidad_id}
                  onChange={e => setForm(f => ({ ...f, comunidad_id: e.target.value }))}
                  required
                >
                  <option value="">Seleccionar</option>
                  {comunidades.map(c => (
                    <option key={c.id} value={c.id}>{c.nombre}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
              <label className="form-label">Email (opcional)</label>
              <input
                type="email"
                className="form-control"
                placeholder="vecino@email.com"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              />
            </div>

            <div className="form-group" style={{ flex: '0 1 120px', marginBottom: 0 }}>
              <label className="form-label">Num usos</label>
              <input
                type="number"
                className="form-control"
                min={1}
                max={100}
                value={form.usos_maximos}
                onChange={e => setForm(f => ({ ...f, usos_maximos: e.target.value }))}
              />
            </div>

            <div className="form-group" style={{ flex: '0 1 130px', marginBottom: 0 }}>
              <label className="form-label">Expira (dias)</label>
              <input
                type="number"
                className="form-control"
                min={0}
                placeholder="Sin limite"
                value={form.dias_expiracion}
                onChange={e => setForm(f => ({ ...f, dias_expiracion: e.target.value }))}
              />
            </div>

            <div style={{ flex: '0 0 auto' }}>
              <button type="submit" className="btn btn-primary" disabled={creando}>
                {creando ? 'Creando...' : 'Generar link'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-secondary)', textAlign: 'center' }}>Cargando...</p>
      ) : invitaciones.length === 0 ? (
        <p style={{ color: 'var(--text-secondary)', textAlign: 'center' }}>No hay invitaciones creadas todavia.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {invitaciones.map(inv => {
            const agotada = estaAgotada(inv);
            const expirada = estaExpirada(inv);
            const inactiva = agotada || expirada || !inv.activo;
            return (
              <div
                key={inv.id}
                className="card"
                style={{
                  padding: '14px 18px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 12,
                  opacity: inactiva ? 0.55 : 1,
                  borderLeft: inactiva ? '3px solid #e74c3c' : '3px solid var(--primary-color, #2563eb)',
                }}
              >
                <div style={{ flex: '1 1 260px' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--text-primary)', marginBottom: 4 }}>
                    {inv.comunidad_nombre || 'Comunidad'}
                    {inv.email && (
                      <span style={{ marginLeft: 8, fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 400 }}>
                        Solo para {inv.email}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginBottom: 6 }}>
                    <span>Usos: {inv.usos_actuales}/{inv.usos_maximos}</span>
                    <span>Expira: {formatFecha(inv.expira_en)}</span>
                    {agotada && <span style={{ color: '#e74c3c', fontWeight: 600 }}>Agotado</span>}
                    {expirada && !agotada && <span style={{ color: '#e74c3c', fontWeight: 600 }}>Expirado</span>}
                  </div>
                  <div style={{
                    background: 'var(--bg-secondary, #f5f5f5)',
                    border: '1px solid var(--border-color, #ddd)',
                    borderRadius: 6,
                    padding: '5px 10px',
                    fontSize: '0.76rem',
                    color: 'var(--primary-color, #2563eb)',
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                  }}>
                    {BASE_URL}/registro?invite={inv.token}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  {!inactiva && (
                    <button
                      className="btn btn-secondary"
                      style={{ fontSize: '0.82rem', padding: '6px 14px' }}
                      onClick={() => copiarLink(inv.token)}
                    >
                      {linkCopiado === inv.token ? 'Copiado' : 'Copiar link'}
                    </button>
                  )}
                  <button
                    className="btn"
                    style={{ fontSize: '0.82rem', padding: '6px 14px', background: '#e74c3c', color: '#fff', border: 'none' }}
                    onClick={() => handleEliminar(inv.id)}
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
        confirmLabel="Eliminar"
        variant="danger"
      />
    </div>
  );
}

export default Invitaciones;
